import { useEffect, useId, useRef } from 'react'
import type { CurrentInsightSetup } from '@testfeed/shopify-contracts'
import { insightOpportunityCopy as copy } from '@testfeed/product-language'
import type { InsightModalElement } from './useInsightOpportunityDetails'
import { ActionButton, type ActionButtonLifecycle } from '../ActionButton'
import { useAppBridge } from '@shopify/app-bridge-react'
import { trackShopifyAction } from '../../shopifyUiTracking'
import { InsightOpportunityResearch } from './InsightOpportunityResearch'

/** Always mounted; keep content through hide animation. Opening and reading never seed a draft. */
export function InsightOpportunityDetails({
  setup,
  open,
  available,
  lifecycle,
  error,
  onAfterHide,
  onTest,
}: {
  setup: CurrentInsightSetup | null
  open: boolean
  available: boolean
  lifecycle: ActionButtonLifecycle<'test-setup'>
  error?: string
  onAfterHide: () => void
  onTest: () => void
}) {
  const shopify = useAppBridge()
  const id = `insight-opportunity-details-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
  const modal = useRef<InsightModalElement>(null)
  const shown = useRef(false)
  useEffect(() => {
    let cancelled = false
    void customElements.whenDefined('s-modal').then(() => {
      if (cancelled) return
      if (open) {
        shown.current = true
        modal.current?.showOverlay()
      } else if (shown.current) modal.current?.hideOverlay()
    })
    return () => {
      cancelled = true
    }
  }, [open])
  useEffect(() => {
    const element = modal.current
    const hidden = () => {
      shown.current = false
      onAfterHide()
    }
    element?.addEventListener('afterhide', hidden)
    return () => element?.removeEventListener('afterhide', hidden)
  }, [onAfterHide])
  let heading: string = copy.details
  let content = null
  let action = null
  if (setup) {
    heading = copy.comparisonLabel(setup.productTitle)
    let failure = null
    if (error) failure = <s-banner tone="critical">{error}</s-banner>
    else if (!available) failure = <s-banner tone="warning">{copy.noLongerListed}</s-banner>
    content = (
      <s-stack gap="base">
        {failure}
        <s-text fontWeight="semibold">{setup.question}</s-text>
        <s-text color="subdued">{copy.currentFinding(setup.figure.customers, setup.figure.buyers)}</s-text>
        <s-text color="subdued">
          {copy.preparedEvidence(
            setup.preparedEvidence.customers,
            setup.preparedEvidence.buyers,
            setup.preparedEvidence.ordersSyncedAt,
          )}
        </s-text>
        <s-stack gap="small-100">
          <s-text fontWeight="medium">{copy.why}</s-text>
          <s-paragraph>{setup.reason}</s-paragraph>
        </s-stack>
        <s-text>
          {copy.audience}: {setup.audienceLabel}
        </s-text>
        <s-divider />
        <s-stack gap="small-100">
          <s-text fontWeight="semibold">{copy.current(setup.fieldLabel)}</s-text>
          <s-paragraph>{setup.current}</s-paragraph>
        </s-stack>
        <s-stack gap="small">
          <s-text fontWeight="semibold">{copy.alternatives}</s-text>
          {setup.alternatives.map((alternative) => (
            <s-stack key={alternative.title} gap="small-100">
              <s-text fontWeight="medium">{alternative.title}</s-text>
              <s-paragraph>{alternative.value}</s-paragraph>
            </s-stack>
          ))}
        </s-stack>
        <s-text color="subdued">{copy.caveat}</s-text>
        <InsightOpportunityResearch research={setup.research} />
        <s-paragraph>{copy.draftConfirmation}</s-paragraph>
      </s-stack>
    )
    action = (
      <ActionButton slot="primary-action" lifecycle={lifecycle} action="test-setup" variant="primary" onClick={onTest}>
        {copy.test}
      </ActionButton>
    )
  }
  return (
    <s-modal ref={modal as never} id={id} heading={heading} size="large">
      {content}
      {action}
      <s-button
        slot="secondary-actions"
        commandFor={id}
        command="--hide"
        onClick={() => trackShopifyAction(shopify, 'close-opportunity', () => undefined)}
      >
        {copy.close}
      </s-button>
    </s-modal>
  )
}
